// frontend_MainDashboard/src/hooks/api/useChatApi.ts

import { useCallback } from 'react';
import { useApiBase } from './useApiBase';
import {
  ChatListResponse,
  MessagesResponse,
  SendMessagePayload,
  ToggleBotPausePayload,
  Message as FrontendMessage,
  ChatMessagesSearchRequest,
  ChatMessagesSearchResponse,
  TranslateChatRequestPayload,
  TranslateChatResponse,
  SendMediaRequest,
} from '@openmirror/shared';

export interface GetClientChatsOptions {
  limit?: number;
  cursor?: string;
}

export interface ChatApiHook {
  getClientChats: (options?: GetClientChatsOptions) => Promise<ChatListResponse>;
  getChatMessages: (endUserWaId: string, options?: { signal?: AbortSignal }) => Promise<MessagesResponse>;
  sendOperatorMessage: (endUserWaId: string, payload: SendMessagePayload) => Promise<FrontendMessage>;
  sendOperatorMedia: (endUserWaId: string, file: File, payload: SendMediaRequest) => Promise<FrontendMessage>;
  toggleBotPause: (endUserWaId: string, payload: ToggleBotPausePayload) => Promise<{ success: boolean; botPaused: boolean }>;
  toggleGlobalBotPause: (payload: ToggleBotPausePayload) => Promise<{ success: boolean; message: string }>;
  archiveChat: (endUserWaId: string) => Promise<{ success: boolean; message?: string }>;
  deleteChat: (endUserWaId: string) => Promise<{ success: boolean; message?: string }>;
  getArchivedChats: (options?: GetClientChatsOptions) => Promise<ChatListResponse>;
  getArchivedChatMessages: (endUserWaId: string, options?: { signal?: AbortSignal }) => Promise<MessagesResponse>;
  restoreChat: (endUserWaId: string) => Promise<{ success: boolean; message?: string }>;
  searchChatMessages: (payload: ChatMessagesSearchRequest, options?: { signal?: AbortSignal }) => Promise<ChatMessagesSearchResponse>;
  translateChatMessages: (endUserWaId: string, payload: TranslateChatRequestPayload) => Promise<TranslateChatResponse>;
}

export const useChatApi = (): ChatApiHook => {
  const { makeRequest, makeFormDataRequest } = useApiBase();

  const getClientChats = useCallback((options: GetClientChatsOptions = {}): Promise<ChatListResponse> => {
    const params = new URLSearchParams();
    if (options.limit) {
        params.set('limit', String(options.limit));
    }
    if (options.cursor) {
        params.set('cursor', options.cursor);
    }
    const query = params.toString();
    const endpoint = query ? `/client/chats?${query}` : '/client/chats';
    
    return makeRequest<ChatListResponse>(endpoint, 'GET');
  }, [makeRequest]);

  const getChatMessages = useCallback((endUserWaId: string, options?: { signal?: AbortSignal }): Promise<MessagesResponse> => {
    return makeRequest<MessagesResponse>(`/client/chats/${endUserWaId}/messages`, 'GET', undefined, options);
  }, [makeRequest]);

  const sendOperatorMessage = useCallback((endUserWaId: string, payload: SendMessagePayload): Promise<FrontendMessage> => {
    return makeRequest<FrontendMessage, SendMessagePayload>(`/client/chats/${endUserWaId}/send_message`, 'POST', payload);
  }, [makeRequest]);

  const sendOperatorMedia = useCallback((endUserWaId: string, file: File, payload: SendMediaRequest): Promise<FrontendMessage> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', payload.type);
    if (payload.caption) formData.append('caption', payload.caption);
    return makeFormDataRequest<FrontendMessage>(`/client/chats/${endUserWaId}/send_media`, 'POST', formData);
  }, [makeFormDataRequest]);

  const toggleBotPause = useCallback((endUserWaId: string, payload: ToggleBotPausePayload): Promise<{ success: boolean; botPaused: boolean }> => {
    return makeRequest<{ success: boolean; botPaused: boolean }, ToggleBotPausePayload>(`/client/chats/${endUserWaId}/toggle_bot_pause`, 'POST', payload);
  }, [makeRequest]);

  const toggleGlobalBotPause = useCallback((payload: ToggleBotPausePayload): Promise<{ success: boolean; message: string }> => {
    return makeRequest<{ success: boolean; message: string }, ToggleBotPausePayload>('/client/toggle_global_bot_pause', 'POST', payload);
  }, [makeRequest]);

  const archiveChat = useCallback((endUserWaId: string): Promise<{ success: boolean; message?: string }> => {
    return makeRequest<{ success: boolean; message?: string }>(`/client/chats/${endUserWaId}/archive`, 'POST');
  }, [makeRequest]);

  const deleteChat = useCallback((endUserWaId: string): Promise<{ success: boolean; message?: string }> => {
    return makeRequest<{ success: boolean; message?: string }>(`/client/chats/${endUserWaId}`, 'DELETE');
  }, [makeRequest]);

  const getArchivedChats = useCallback((options: GetClientChatsOptions = {}): Promise<ChatListResponse> => {
    const params = new URLSearchParams();
    if (options.limit) {
        params.set('limit', String(options.limit));
    }
    if (options.cursor) {
        params.set('cursor', options.cursor);
    }
    const query = params.toString();
    const endpoint = query ? `/client/archived_chats?${query}` : '/client/archived_chats';
    return makeRequest<ChatListResponse>(endpoint, 'GET');
  }, [makeRequest]);

  const getArchivedChatMessages = useCallback((endUserWaId: string, options?: { signal?: AbortSignal }): Promise<MessagesResponse> => {
    return makeRequest<MessagesResponse>(`/client/archived_chats/${endUserWaId}/messages`, 'GET', undefined, options);
  }, [makeRequest]);

  const searchChatMessages = useCallback((payload: ChatMessagesSearchRequest, options?: { signal?: AbortSignal }): Promise<ChatMessagesSearchResponse> => {
    return makeRequest<ChatMessagesSearchResponse, ChatMessagesSearchRequest>('/client/chats/search_messages', 'POST', payload, options);
  }, [makeRequest]);

  const restoreChat = useCallback((endUserWaId: string): Promise<{ success: boolean; message?: string }> => {
    return makeRequest<{ success: boolean; message?: string }>(`/client/chats/${endUserWaId}/restore`, 'POST');
  }, [makeRequest]);

  const translateChatMessages = useCallback((endUserWaId: string, payload: TranslateChatRequestPayload): Promise<TranslateChatResponse> => {
    return makeRequest<TranslateChatResponse, TranslateChatRequestPayload>(`/client/chats/${endUserWaId}/translate`, 'POST', payload);
  }, [makeRequest]);

  return {
    getClientChats,
    getChatMessages,
    sendOperatorMessage,
    sendOperatorMedia,
    toggleBotPause,
    toggleGlobalBotPause,
    archiveChat,
    deleteChat,
    getArchivedChats,
    getArchivedChatMessages,
    restoreChat,
    searchChatMessages,
    translateChatMessages,
  };
};
